import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Text } from "@/components/ui/text";
import type { ChatMemo } from "./types";
import dayjs from "dayjs";
import { AI_CHAT_PATHNAME } from "@/constants/pathname";

interface ChatMemoDetailProps {
  memo: ChatMemo;
  chatTitle: string;
  onBack: () => void;
  onDelete: () => void;
  onEdit: (memo: ChatMemo) => void;
}

const ChatMemoDetail = ({
  memo,
  chatTitle,
  onBack,
  onDelete,
  onEdit,
}: ChatMemoDetailProps) => {
  const { id: chatId } = useParams();
  const navigate = useNavigate();
  const [editedTitle, setEditedTitle] = useState(memo.title);
  const [editedContent, setEditedContent] = useState(memo.content);

  const handleBack = () => {
    onEdit({
      ...memo,
      title: editedTitle || "Untitled",
      content: editedContent,
    });
    onBack();
  };

  const handleGoToSource = () => {
    if (!memo.chatHistoryId || !chatId) return;
    // 해당 채팅 페이지로 이동하고 메시지 ID를 쿼리 파라미터로 전달
    navigate(`${AI_CHAT_PATHNAME}/${chatId}?messageId=${memo.chatHistoryId}`);
  };

  return (
    <div className="flex h-full flex-col gap-6 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6">
        <button
          onClick={handleBack}
          className="flex h-10 w-10 items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
          >
            <path
              d="M23.332 28.333L14.9987 19.9997L23.332 11.6663"
              stroke="#666666"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Text variant="title-md">{chatTitle}</Text>
        <button
          onClick={onDelete}
          className="flex h-10 w-10 items-center justify-center bg-grey-95 rounded-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="18"
            viewBox="0 0 16 18"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.31244 0C5.28083 0 4.44455 0.836282 4.44455 1.86789V2.62161C4.44455 2.63673 4.44491 2.65177 4.44562 2.66672H0.888889C0.397969 2.66672 0 3.06469 0 3.55561V5.33339C0 5.82431 0.39797 6.22228 0.888889 6.22228H1.36621L2.09087 15.6428C2.18354 16.8476 3.18811 17.7778 4.39639 17.7778H11.6036C12.8119 17.7778 13.8165 16.8476 13.9091 15.6428L14.6338 6.22228H15.1111C15.602 6.22228 16 5.82431 16 5.33339V3.55561C16 3.06469 15.602 2.66672 15.1111 2.66672H11.5546C11.5553 2.65177 11.5557 2.63673 11.5557 2.62161V1.86789C11.5557 0.836282 10.7194 0 9.68777 0H6.31244Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.68777 0.888889H6.31244C5.77175 0.888889 5.33344 1.3272 5.33344 1.86789V2.62161C5.33344 2.64649 5.35361 2.66667 5.3785 2.66667H10.6217C10.6466 2.66667 10.6668 2.64649 10.6668 2.62161V1.86789C10.6668 1.3272 10.2285 0.888889 9.68777 0.888889ZM6.31244 0C5.28083 0 4.44455 0.836282 4.44455 1.86789V2.62161C4.44455 2.63673 4.44491 2.65177 4.44562 2.66672H0.888889C0.397969 2.66672 0 3.06469 0 3.55561V5.33339C0 5.82431 0.39797 6.22228 0.888889 6.22228H1.36621L2.09087 15.6428C2.18354 16.8476 3.18811 17.7778 4.39639 17.7778H11.6036C12.8119 17.7778 13.8165 16.8476 13.9091 15.6428L14.6338 6.22228H15.1111C15.602 6.22228 16 5.82431 16 5.33339V3.55561C16 3.06469 15.602 2.66672 15.1111 2.66672H11.5546C11.5553 2.65177 11.5557 2.63673 11.5557 2.62161V1.86789C11.5557 0.836282 10.7194 0 9.68777 0H6.31244ZM13.7423 6.22228H2.25772L2.97714 15.5747C3.03419 16.3163 3.65259 16.8889 4.39639 16.8889H11.6036C12.3474 16.8889 12.9658 16.3163 13.0229 15.5747L13.7423 6.22228ZM14.7022 5.33339H15.1111V3.55561H0.888889V5.33339H1.29783H1.77778H14.2222H14.7022ZM6.39022 13.6546C6.14633 13.6824 5.9261 13.5072 5.89832 13.2633L5.40216 8.90816C5.37438 8.66428 5.54956 8.44405 5.79344 8.41627C6.03732 8.38849 6.25755 8.56367 6.28534 8.80755L6.7815 13.1627C6.80928 13.4066 6.6341 13.6268 6.39022 13.6546ZM9.48708 13.6546C9.73097 13.6824 9.95119 13.5072 9.97898 13.2633L10.4751 8.90816C10.5029 8.66428 10.3277 8.44405 10.0839 8.41627C9.83997 8.38849 9.61975 8.56367 9.59196 8.80755L9.0958 13.1627C9.06802 13.4066 9.2432 13.6268 9.48708 13.6546Z"
              fill="black"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6">
        <div className="whitespace-pre-wrap">
          <div className="flex flex-col gap-1">
            <textarea
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="resize-none w-full outline-none font-pretendard text-[18px] font-bold leading-[26px] tracking-[0.15px]"
            />
            <Text variant="body-light" className="mb-4 text-grey-50">
              {dayjs(memo.updatedAt).format("YYYY.MM.DD HH:mm")}
            </Text>
          </div>
          {memo.chatHistoryId && (
            <button
              onClick={handleGoToSource}
              className="mb-6 mt-2 flex items-center gap-2 bg-grey-95 py-2 px-6 rounded-md"
            >
              <Text variant="label-lg">Go to source</Text>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M6 12L10 8L6 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          <div>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="resize-none w-full h-[calc(100vh-330px)] outline-none font-pretendard text-[15px] font-normal leading-[26px] tracking-[0.15px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMemoDetail;
